import { useMemo } from 'react';
import { AudienceDownloadTrigger, FilterCard } from '@/components';
import { VisibleFilterKey } from '@/components/cards/FilterCard/Filters';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { twMerge } from 'tailwind-merge';
import { useProfilesQuery } from '@/services/revenueApi';
import { useFilterContext, useDerivedNetworkContext } from '@/context';
import NoAccessMessage from '@/components/PageWrapper/NoAccessMessage';
import { cardFullWidth, cardOneHalf } from '@/utils/styleUtils';
import AgeCard from './AgeCard';
import GenderCard from './GenderCard';
import LocationCard from './LocationCard';
import TopPlayersCardEcho from './TopPlayersCardEcho';
import TopDevicesCard from './TopDevicesCard';
import SocialProfilesCard from './SocialProfilesCard/SocialProfilesCard';
import FollowersCard from './FollowersCard';
import UniqueListenersCard from './UniqueListenersCard';
import { renderAudienceBanner } from './renderAudienceBanner';
import useFetchQuery from '@/hooks/useFetchQuery';
import { useSyncUrlToFilters } from '@/hooks/useSyncUrlToFilters';
import useHasHlsEnabledNetwork from '@/hooks/useHasHlsEnabledNetwork';
import { isSocialPlatform, Platform } from '@/types/api/platforms';
import { Heading } from '@acast-tech/decibel';

const AudiencePage = () => {
  const { filters } = useFilterContext();
  const { selectedNetwork } = useDerivedNetworkContext();
  const { shows } = selectedNetwork;
  const {
    audienceTab: isAudienceTabEnabled = false,
    contentPerformanceHls = false,
    audiencePageDataBanners = [],
  } = useFlags<{
    audienceTab?: boolean;
    contentPerformanceHls?: boolean;
    audiencePageDataBanners?: string[];
  }>();
  const hasHlsNetwork = useHasHlsEnabledNetwork();
  const isAudienceEnabled = isAudienceTabEnabled || (contentPerformanceHls && hasHlsNetwork);
  const { isSynced } = useSyncUrlToFilters({ requireSingleShow: true });
  const { queryResult } = useFetchQuery(useProfilesQuery, {
    enabled: filters.selectedShows.length > 0 && isSynced,
  });

  const allPlatformsShowing =
    [...new Set(queryResult.data?.profiles.map((profile) => profile.platform.toLowerCase()) ?? [])]
      .length === 3;

  const platformsWithoutData = useMemo(() => {
    if (queryResult.isPending || queryResult.isError) {
      return [];
    }

    const connectedSocialProfiles =
      queryResult.data?.profiles.filter((profile) => isSocialPlatform(profile.platform)) ?? [];

    if (connectedSocialProfiles.length === 0) return [];

    const profilesWithNoData = connectedSocialProfiles.filter((p) => !p.earliest_data_date);

    return [...new Set(profilesWithNoData.map((p) => p.platform))];
  }, [queryResult.isPending, queryResult.isError, queryResult.data]);

  const platformsBeforeEarliestData = useMemo(() => {
    if (queryResult.isPending || queryResult.isError) return [];
    const rangeFrom = filters.dateRange.from;
    return (queryResult.data?.profiles ?? [])
      .filter(
        (p) =>
          isSocialPlatform(p.platform) && p.earliest_data_date && rangeFrom < p.earliest_data_date
      )
      .map((p) => ({
        platform: p.platform,
        earliestDataDate: p.earliest_data_date ?? rangeFrom,
      }));
  }, [queryResult.isPending, queryResult.isError, queryResult.data, filters.dateRange.from]);

  // Hour-precision UTC prefix (YYYY-MM-DDTHH). Sliced to a string so it's a stable
  // useMemo dependency that only changes once an hour, rather than every render.
  // expires_at is currently a truncated UTC date (YYYY-MM-DD) but will become a full
  // UTC timestamp upstream; both compare correctly against this prefix lexicographically.
  const nowUtcPrefix = new Date().toISOString().slice(0, 13);
  const { reauthRequiredPlatforms, reauthRequiredLatestExpiry } = useMemo(() => {
    if (queryResult.isPending || queryResult.isError) {
      return { reauthRequiredPlatforms: [], reauthRequiredLatestExpiry: undefined };
    }
    const expiredProfiles =
      queryResult.data?.profiles.filter(
        (p) => isSocialPlatform(p.platform) && p.expires_at != null && p.expires_at < nowUtcPrefix
      ) ?? [];
    return {
      reauthRequiredPlatforms: [...new Set(expiredProfiles.map((p) => p.platform))],
      // Latest expiry among the *expired* platforms only — used as the dismissal
      // threshold so a newer expiry re-shows the banner. Including non-expired tokens
      // here would push the threshold into the future and make dismissal impossible.
      reauthRequiredLatestExpiry: expiredProfiles
        .map((p) => p.expires_at as string)
        .reduce<
          string | undefined
        >((latest, e) => (latest == null || e > latest ? e : latest), undefined),
    };
  }, [queryResult.isPending, queryResult.isError, queryResult.data, nowUtcPrefix]);

  // Pick the highest-priority banner whose activation criteria are met (or none).
  const audienceBanner = renderAudienceBanner({
    isPending: queryResult.isPending,
    enabledBanners: audiencePageDataBanners,
    showId: filters.selectedShows[0] ?? '',
    networkId: selectedNetwork?.id,
    platformsWithoutData,
    isOnMarketplace: queryResult.data?.show?.is_on_marketplace,
    hasMarketplacePodcastProfile: queryResult.data?.show?.has_marketplace_podcast_profile,
    hasPhylloConfigured: queryResult.data?.show?.has_phyllo_configured,
    platformsBeforeEarliestData,
    reauthRequiredPlatforms,
    reauthRequiredLatestExpiry,
  });

  const noOfShows = shows?.length ?? 0;
  const visibleFilters: VisibleFilterKey[] =
    noOfShows > 1
      ? [VisibleFilterKey.Networks, VisibleFilterKey.Shows, VisibleFilterKey.DateRange]
      : [VisibleFilterKey.Networks, VisibleFilterKey.DateRange];

  if ((!isAudienceEnabled && selectedNetwork?.id) || (shows && shows.length < 1)) {
    return <NoAccessMessage />;
  }

  return (
    <>
      <div className="pt-6 pb-10 flex flex-col md:flex-row gap-4 justify-between md:items-center">
        <Heading variant="h1" mb={0}>
          Audience
        </Heading>
        <div className="flex justify-end">
          <AudienceDownloadTrigger />
        </div>
      </div>

      <FilterCard visibleFilters={visibleFilters} />

      {/* Card grid */}
      <section className="mb-6">
        <SocialProfilesCard
          className={'w-full p-0 overflow-hidden'}
          hideProfileBanner={!!audiencePageDataBanners?.length}
        />
        {audienceBanner}
      </section>

      <section className="w-full flex flex-col lg:flex-row gap-4 mb-6">
        {/* TODO: figure out how to do this without the number of platforms showing */}
        <UniqueListenersCard className={twMerge('w-full', allPlatformsShowing && 'lg:basis-2/5')} />
        <FollowersCard
          platform={Platform.YouTube}
          className={twMerge('w-full', allPlatformsShowing && 'lg:basis-1/5')}
        />
        <FollowersCard
          platform={Platform.TikTok}
          className={twMerge('w-full', allPlatformsShowing && 'lg:basis-1/5')}
        />
        <FollowersCard
          platform={Platform.Instagram}
          className={twMerge('w-full', allPlatformsShowing && 'lg:basis-1/5')}
        />
      </section>

      <section className="grid grid-cols-12 grid-rows-auto gap-6 mb-6">
        <GenderCard className={cardOneHalf} />
        <AgeCard className={cardOneHalf} />
        <LocationCard className={cardFullWidth} />
      </section>

      <section className="grid grid-cols-12 grid-rows-auto gap-6">
        <TopPlayersCardEcho className={cardOneHalf} />
        <TopDevicesCard className={cardOneHalf} />
      </section>
    </>
  );
};

export default AudiencePage;
